(ns basic-components.html-table.table
  (:require
   [frontend.utils :as fu]
   [common.util :as cu]
   [basic-components.html-table.cell :as table-cell]
   [common.localization :as loc]
   [basic-components.html-table.header-cell :as table-header]
   [clojure.string :as string]
   [frontend.state.current-session :as session]
   [common.schema.registry :as registry]))

(defn resolve-groups
  [data group-by-key]
  [[nil data]])

(def complex-data-types
  [:data.type/text-edn
   :data.type/financials
   :data.type/tesi-financials
   :data.type/tesi-financials-2])

(defn render-empty-cell?
  [cell-data-type]
  (some #(= cell-data-type %) complex-data-types))


(defn table-head
  [{:keys [styles sorted-columns table-key state]}]
  [:thead

   [:tr
    {:class "*:px-3 *:py-3.5 *:text-left *:text-sm *:font-semibold *:text-base-light-text"}
    (->> sorted-columns
         (map (fn [c]
                (let [column-renderer (get c :column/header-renderer table-header/cell)
                      column-class-name (:column/class-name c)]
                  ^{:key (str table-key "-header-" (:column/key c))}
                  [:th

                   [column-renderer
                    {:column-meta c
                     :set-sort!   (fn [[sort-key direction]]
                                    (swap! state assoc
                                      :sort-by-key sort-key
                                      :sort-direction direction))
                     :state       state}]])))
         (doall))]])

(defn apply-sort-order
  [sort-direction data]
  (if (= sort-direction :asc)
    (reverse data)
    data))

(defn table-row
  [{:keys [state row-data sorted-columns table-key
           group-key id-key on-row-select]}]
  [:tr
   {:class "*:px-3 *:py-4 *:whitespace-nowrap *:text-sm *:text-base-light-text *:align-middle [&>*:last-child]:pr-0"}
   (->> sorted-columns
        (map
         (fn [c]
           (let [cell-renderer (get c :column/cell-renderer table-cell/cell)
                 cell-value (get row-data (:column/key c))
                 cell-data-type (:column/data-type c)
                 column-class-name (:column/class-name c)
                 cell-style-fn (:column/cell-style-fn c)
                 field-schema (:field-schema row-data)]
             ^{:key (str table-key "-" group-key "-" (get row-data id-key) "-" (:column/key c))}
             [:td
              {:style      (if cell-style-fn
                             (cell-style-fn {:column-meta      c
                                             :default-renderer table-cell/cell
                                             :cell-value       cell-value
                                             :row-data         row-data
                                             :state            state
                                             :on-row-select    on-row-select
                                             :field-schema     field-schema})
                             {})}
              (if (render-empty-cell? cell-data-type)
                (loc/tr :table-empty-cell)
                [cell-renderer
                 {:column-meta      c
                  :tenant-schema    @session/tenant-schema
                  :default-renderer table-cell/cell
                  :cell-value       cell-value
                  :row-data         row-data
                  :state            state
                  :on-row-select    on-row-select
                  :field-schema     field-schema}])])))
        (doall))])

(defn table-body
  [{:keys [styles sorted-columns table-key group-key id-key state data on-row-select
           use-external-sort?]}]
  (let [{:keys [sort-by-key sort-direction]} @state
        sorted-data (if use-external-sort?
                      data
                      (->> data
                           (sort-by
                            (fn [row-data]
                              (let [column-config (->> sorted-columns
                                                       (cu/filter-by :column/key sort-by-key)
                                                       (first))
                                    custom-sort-fn (:column/sort-fn column-config)
                                    value-getter-fn (:column/value-getter column-config)
                                    value (if (fn? value-getter-fn)
                                            (value-getter-fn row-data)
                                            (get row-data sort-by-key))]
                                (if (fn? custom-sort-fn)
                                  (custom-sort-fn value)
                                  (string/lower-case (str value))))))
                           (apply-sort-order sort-direction)))]
    [:tbody
     {:class "divide-y divide-solid divide-base-light-border-paler"}
     (->> sorted-data
          (map (fn [row-data]
                 ^{:key (str table-key "-" group-key "-" (get row-data id-key))}
                 [table-row
                  {:state          state
                   :row-data       row-data
                   :sorted-columns sorted-columns
                   :table-key      table-key
                   :group-key      group-key
                   :id-key         id-key
                   :on-row-select  on-row-select}]))
          (doall))]))

(defn resolve-group-label
  [group-value]
  (cond (keyword? group-value)
        (loc/field-name group-value
                        (registry/get-field-schema @session/tenant-schema group-value))
        (and (number? group-value)
             (= :entity.type/named-value (:entity/type (session/get-entity group-value))))
        (:named-value/value (session/get-entity group-value))
        :else
        group-value))

(defn view
  [data options]
  (let [state (:state options)]
    (swap! state assoc
      :sort-by-key (get-in options [:settings :default-sort-key])
      :column-order (get-in options [:settings :column-order]))
    (fn [data options]
      (let [{:keys [column-order]} @state
            {:keys [id-key table-key columns styles settings state group-by-key use-external-sort?]} options
            grouped-data (when group-by-key
                           (->> data
                                (group-by group-by-key)))
            on-row-select (:on-row-select settings)
            sorted-columns
            (->> columns
                 (sort (fn [a b]
                         (let [group-a (:column/group a)
                               group-b (:column/group b)]
                           (if (not= group-a group-b)
                             (compare group-a group-b)
                             (compare (cu/index-of column-order (:column/key a))
                                      (cu/index-of column-order (:column/key b))))))))]
        [:table
         {:class "min-w-full divide-y divide-solid divide-base-light-border-paler"}
         [table-head
          {:styles         styles
           :sorted-columns sorted-columns
           :table-key      table-key
           :state          state}]
         (if-not grouped-data
           [table-body
            {:styles         styles
             :sorted-columns sorted-columns
             :table-key      table-key
             :id-key         id-key
             :data           data
             :state          state
             :on-row-select  on-row-select
             :use-external-sort? use-external-sort?}]
           ;; TODO: in the future we should control group value and sorting from the outside
           ;; instead of hardcoding in here
           (->> grouped-data
                (sort-by (fn [[group-value _]]
                           (resolve-group-label group-value)))
                (mapcat
                 (fn [[group-value group-data]]
                   [^{:key (str table-key "-" group-value "-header")}
                    [:tbody
                     {:class "divide-y divide-solid divide-base-light-border-paler"}
                     [:tr
                      {:class "*:px-3 *:py-4 *:whitespace-nowrap *:text-sm *:text-base-light-text *:align-middle *:last:pr-0"}
                      [:td
                       {:col-span   (count sorted-columns)}
                       [:h2
                        {:class "font-bold text-md"}
                        (resolve-group-label group-value)]]]]
                    ^{:key (str table-key "-" group-value)}
                    [table-body
                     {:styles         styles
                      :sorted-columns sorted-columns
                      :table-key      table-key
                      :id-key         id-key
                      :data           group-data
                      :state          state
                      :on-row-select  on-row-select}]]))
                (doall)))]))))
